import Swiper, { Grid, Navigation } from 'swiper';

const heightControllers = new WeakMap();
const titleControllers = new WeakMap();

const getGridRows = (slidesCount) => {
  if (slidesCount <= 3) {
    return 1;
  }

  if (slidesCount <= 6) {
    return 2;
  }

  if (slidesCount >= 13) {
    return 4;
  }

  return 3;
};

const interpolate = (value, start, end, startValue, endValue) => {
  if (value <= start) {
    return startValue;
  }

  if (value >= end) {
    return endValue;
  }

  return startValue + (value - start) * (endValue - startValue) / (end - start);
};

const getHorizontalGap = () => {
  const viewportWidth = window.innerWidth;

  if (viewportWidth <= 1024) {
    return Math.round(interpolate(viewportWidth, 640, 1024, 20, 30));
  }

  return Math.round(interpolate(viewportWidth, 1024, 1440, 30, 80));
};

const getVerticalGap = () => Math.round(interpolate(window.innerWidth, 640, 1440, 20, 61));

const createLineTitlesController = (swiperEl, swiper) => {
  if (typeof window.lineTitlesFunc !== 'function') {
    return null;
  }

  const titles = Array.from(swiperEl.querySelectorAll('.choice-sec__item-title'));

  if (!titles.length) {
    return null;
  }

  const getGroups = () => {
    const rows = Math.max(1, Number(swiper.params.grid?.rows) || 1);
    const groups = Array.from({ length: rows }, () => []);

    Array.from(swiperEl.querySelectorAll('.swiper-slide')).forEach((slide, index) => {
      const title = slide.querySelector('.choice-sec__item-title');

      if (title) {
        groups[index % rows].push(title);
      }
    });

    return groups;
  };

  return window.lineTitlesFunc(titles, { getGroups });
};

const createHeightController = (swiperEl, swiper) => {
  let resizeTimeout = null;
  let resizeFrame = null;

  const applyVerticalGap = () => {
    const rows = swiper.params.grid.rows;
    const verticalGap = getVerticalGap();

    Array.from(swiperEl.querySelectorAll('.swiper-slide')).forEach((slide, index) => {
      slide.classList.toggle('choice-swiper-row-offset', Boolean(index % rows));
    });
    swiperEl.style.setProperty('--choice-swiper-row-gap', `${verticalGap}px`);

    return verticalGap;
  };

  const getSlideRowGroups = () => {
    const rows = Math.max(1, Number(swiper.params.grid?.rows) || 1);
    const groups = Array.from({ length: rows }, () => []);

    Array.from(swiperEl.querySelectorAll('.swiper-slide')).forEach((slide, index) => {
      groups[index % rows].push(slide);
    });

    return groups.filter((group) => group.length);
  };

  const setStableHeight = () => {
    if (swiper.destroyed) {
      return;
    }

    const wrapperEl = swiperEl.querySelector('.swiper-wrapper');
    const slides = Array.from(swiperEl.querySelectorAll('.swiper-slide'));
    const items = Array.from(swiperEl.querySelectorAll('.choice-sec__item'));

    if (!wrapperEl || !slides.length || !items.length) {
      return;
    }

    swiper.params.spaceBetween = getHorizontalGap();
    swiper.originalParams.spaceBetween = swiper.params.spaceBetween;
    swiperEl.style.height = 'auto';
    wrapperEl.style.height = 'auto';
    slides.forEach((slide) => {
      slide.style.height = 'auto';
    });
    swiper.update();

    const verticalGap = applyVerticalGap();
    const rowGroups = getSlideRowGroups();
    const rowHeights = rowGroups.map((group) => {
      const rowHeight = Math.ceil(Math.max(...group.map((slide) => (
        slide.querySelector('.choice-sec__item')?.offsetHeight || 0
      ))));

      group.forEach((slide) => {
        slide.style.height = `${rowHeight}px`;
      });

      return rowHeight;
    });
    const slideHeight = Math.max(...rowHeights);
    const height = rowHeights.reduce((total, rowHeight) => total + rowHeight, 0)
      + verticalGap * Math.max(0, rowHeights.length - 1);

    swiperEl.style.removeProperty('height');
    wrapperEl.style.removeProperty('height');
    swiperEl.style.setProperty('--choice-swiper-height', `${height}px`);
    swiperEl.style.setProperty('--choice-swiper-slide-height', `${slideHeight}px`);
    swiper.update();
    applyVerticalGap();
  };

  const requestStableHeight = () => {
    if (resizeFrame) {
      cancelAnimationFrame(resizeFrame);
    }

    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = null;
      setStableHeight();
    });
  };

  const onResize = () => {
    if (resizeTimeout) {
      return;
    }

    resizeTimeout = setTimeout(() => {
      resizeTimeout = null;
      requestStableHeight();
    }, 150);
  };

  const onBreakpoint = () => {
    requestStableHeight();
  };

  requestStableHeight();
  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('orientationchange', onResize, { passive: true });
  swiper.on('breakpoint', onBreakpoint);

  if (document.fonts?.ready) {
    document.fonts.ready.then(requestStableHeight).catch(requestStableHeight);
  } else {
    window.addEventListener('load', requestStableHeight, { once: true });
  }

  return {
    requestStableHeight,
    destroy: () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
        resizeTimeout = null;
      }

      if (resizeFrame) {
        cancelAnimationFrame(resizeFrame);
        resizeFrame = null;
      }

      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      swiper.off('breakpoint', onBreakpoint);
    },
  };
};

const initChoiceSwiper = (scope = document) => {
  const wrappers = scope.querySelectorAll('.choice-swiper-wr');

  wrappers.forEach((wrapper) => {
    const swiperEl = wrapper.querySelector('.swiper');

    if (!swiperEl) {
      return;
    }

    const slidesCount = swiperEl.querySelectorAll('.swiper-slide').length;

    if (!slidesCount) {
      return;
    }

    const rows = getGridRows(slidesCount);

    if (swiperEl.swiper) {
      if (swiperEl.swiper.params.grid?.rows === rows) {
        titleControllers.get(wrapper)?.refresh();
        heightControllers.get(wrapper)?.requestStableHeight();
        return;
      }

      titleControllers.get(wrapper)?.destroy();
      titleControllers.delete(wrapper);
      heightControllers.get(wrapper)?.destroy();
      heightControllers.delete(wrapper);
      swiperEl.swiper.destroy(true, true);
    }

    const nextEl = wrapper.querySelector('.swiper-button-next');
    const prevEl = wrapper.querySelector('.swiper-button-prev');
    const swiper = new Swiper(swiperEl, {
      modules: [Grid, Navigation],
      observer: true,
      observeParents: true,
      slidesPerView: 1,
      slidesPerGroup: 1,
      spaceBetween: getHorizontalGap(),
      speed: 500,
      threshold: 10,
      watchOverflow: true,
      grid: {
        rows,
        fill: 'column',
      },
      navigation: nextEl && prevEl ? {
        nextEl,
        prevEl,
      } : undefined,
      breakpoints: {
        641: {
          slidesPerView: 3,
          spaceBetween: 80,
          grid: {
            rows,
            fill: 'column',
          },
        },
      },
    });

    swiper.params.grid.rows = rows;
    swiper.params.grid.fill = 'column';
    swiper.originalParams.grid.rows = rows;
    swiper.originalParams.grid.fill = 'column';
    swiper.update();

    const titleController = createLineTitlesController(swiperEl, swiper);

    if (titleController) {
      titleControllers.set(wrapper, titleController);
      swiper.on('breakpoint', titleController.refresh);
    }

    heightControllers.set(wrapper, createHeightController(swiperEl, swiper));
  });
};

initChoiceSwiper();
window.initChoiceSwiper = initChoiceSwiper;
